import React, { useRef } from "react";
import { useInView } from "framer-motion";
import { ClapperboardIcon, ClockIcon, GiftIcon, GlobeIcon, HourglassIcon, LockIcon, ShieldCheckIcon, ZapIcon } from "lucide-react";
import { PaymentLogos } from "./PaymentLogos";
import { offer, priceStack } from "../data/offer";
import { links } from "../data/images";
import { useOfferCountdown } from "../hooks/useOfferCountdown";
import { BonusSlots } from "./BonusSlots";
import { useVisitorDate } from "../hooks/useVisitorDate";
import { PrimaryButton } from "./PrimaryButton";
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="flex flex-col items-center" data-id="element-178" data-mp="2hupmgu9afko0">
      <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-white font-serif text-3xl font-semibold tabular-nums text-alert sm:h-20 sm:w-20 sm:text-4xl" data-id="element-179" data-mp="2mukcywp5i88j">
        {value}
      </span>
      <span className="mt-2 text-xs font-semibold uppercase tracking-wide text-white sm:text-sm" data-id="element-180" data-mp="1bff75jtq3tkp">{label}</span>
    </div>;
}
const OFFER_SECONDS = 6 * 60;
function remainingText(min: number, sec: number) {
  const m = `${min} ${min === 1 ? "minuto" : "minutos"}`;
  const s = `${sec} ${sec === 1 ? "segundo" : "segundos"}`;
  if (min === 0) return `Faltan ${s} para que desaparezca la oferta`;
  return `Faltan ${m} y ${s} para que desaparezca la oferta`;
}
export function PriceStack() {
  const sectionRef = useRef<HTMLElement>(null);
  const reached = useInView(sectionRef, {
    once: true,
    amount: 0.15
  });
  const {
    minutes,
    seconds,
    minutesNum,
    secondsNum,
    expired,
    time
  } = useOfferCountdown(OFFER_SECONDS, reached);
  const rawDate = useVisitorDate();
  const date = (rawDate.charAt(0).toUpperCase() + rawDate.slice(1)).replace(",", "");
  return <section ref={sectionRef} aria-labelledby="stack-title" className="mt-10 rounded-3xl border-2 border-olive-700 bg-cream px-4 py-8 shadow-lg sm:px-8 sm:py-10" data-id="element-181" data-mp="1peo0j704gkhr">
      <h3 id="stack-title" className="text-center font-serif text-2xl font-semibold text-forest sm:text-3xl" data-id="element-182" data-mp="1zdmzfxv351rk">
        Esto es todo lo que vas a recibir
      </h3>

      <ul className="mx-auto mt-6 max-w-xl" data-id="element-183" data-mp="2be3i6xb69e2j">
        {priceStack.map(row => <li key={row.item} className="flex items-center justify-between gap-4 border-b border-cream-dark py-3 text-base text-cocoa" data-id="element-184" data-mp="2gdpwzb6nf2dg">
            <span data-id="element-185" data-mp="2l5hnxmtpva44">{row.item}</span>
            <span className="shrink-0 text-cocoa-faint line-through" data-id="element-186" data-mp="2l5r7oso64l1d">{row.value}</span>
          </li>)}
        <li className="flex items-center justify-between gap-4 pt-5" data-id="element-187" data-mp="2p8e0avsny9zm">
          <span className="text-base font-bold uppercase text-forest" data-id="element-188" data-mp="2u3jjhmuolak0">Valor total real:</span>
          <span className="shrink-0 text-lg font-bold text-alert line-through" data-id="element-189" data-mp="2z5imgvmie5gf">{offer.totalValue}</span>
        </li>
      </ul>

      <div className="mx-auto mt-6 flex max-w-xl items-center gap-4 rounded-2xl border-2 border-leaf bg-white p-4 sm:gap-5 sm:p-5" data-id="element-190" data-mp="3417qbb5ka8ij">
        <div className="relative flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-gold bg-olive-100" data-id="element-191" data-mp="19ordfri9fw4p">
          <ShieldCheckIcon className="h-9 w-9 text-leaf-price" aria-hidden="true" data-id="element-192" data-mp="3n0u4kifzf5eu" />
          <span className="absolute -bottom-2 rounded-full bg-leaf px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-id="element-193" data-mp="3i810zxw9234c">
            30 días
          </span>
        </div>
        <div className="text-left" data-id="element-194" data-mp="398xfyodivdn7">
          <p className="font-serif text-lg font-semibold leading-snug text-forest sm:text-xl" data-id="element-195" data-mp="399p9ltnk71au">
            Garantía inquebrantable de 30 días
          </p>
          <p className="mt-1 text-sm leading-relaxed text-cocoa-soft" data-id="element-196" data-mp="5qw4844xpirr">
            Probalo sin riesgo. Si no te ayuda a ordenar tus cenas y sentirte mejor por la noche, te devolvemos el{" "}
            <strong className="font-semibold text-cocoa" data-id="element-197" data-mp="3ryvw7rrh4d1x">100% de tu dinero</strong>. Sin preguntas.
          </p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-leaf-price" data-id="element-198" data-mp="2litplipchae5">
            <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-199" data-mp="2gj7a12m1ni26" />
            Compra 100% segura y protegida
          </p>
        </div>
      </div>

      <div className="mt-8 rounded-3xl border border-gold bg-white px-4 py-8 text-center sm:px-8" data-id="element-200" data-mp="88o485k6f6ia">
        <p className="text-base text-cocoa-soft" data-id="element-201" data-mp="d3lmm33cis7u">Precio Especial Por Tiempo Limitado:</p>
        <p className="mt-3 inline-flex items-center gap-2 text-sm font-bold uppercase text-alert sm:text-base" data-id="element-202" data-mp="3vhb9kwfo6ago">
          <ClockIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-203" data-mp="472qyj3qcq7k" />
          Oferta disponible ahora mismo, no pierdas otro día
        </p>
        <p className="mt-1 text-sm text-cocoa-soft sm:text-base" data-id="element-204" data-mp="w0mrzp0taucb">
          {date} · <strong className="font-semibold text-forest" data-id="element-205" data-mp="i0u5k5wbj0a5">{time} hs</strong>
        </p>

        <div className="mx-auto mt-5 max-w-md rounded-2xl bg-alert px-4 py-5 shadow-md" role="timer" aria-live="off" data-id="element-206" data-mp="i2y4e9fd193k">
          <div className="flex items-start justify-center gap-3 sm:gap-4" data-id="element-207" data-mp="32r2oowtr1bye">
            <TimeBox value={minutes} label="Minutos" data-id="element-208" data-mp="32t5og8kpejgb" />
            <span className="pt-4 text-2xl font-bold text-white sm:pt-6" data-id="element-209" data-mp="37qp339lbcy8s">:</span>
            <TimeBox value={seconds} label="Segundos" data-id="element-210" data-mp="305lk2y6zktuy" />
          </div>
          <p className="mt-4 text-sm font-semibold text-white sm:text-base" data-id="element-211" data-mp="2v7a7wgt7y79k">
            {expired ? "El tiempo terminó: la oferta puede desaparecer en cualquier momento" : remainingText(minutesNum, secondsNum)}
          </p>
        </div>

        <div className="mx-auto mt-5 flex max-w-md items-start gap-3 rounded-2xl border-2 border-dashed border-gold bg-cream px-4 py-4 text-left text-sm leading-relaxed text-cocoa sm:text-base" data-id="element-212" data-mp="348rlnsj5lwv3">
          <HourglassIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-213" data-mp="2cidbo7e9h4op" />
          <p data-id="element-214" data-mp="2q9rxsn3gtlzz">
            <strong className="font-semibold text-forest" data-id="element-215" data-mp="2q9i70xaljat8">Solo te toma 2 minutos completar tu compra.</strong> El tiempo
            pasa rápido — <strong className="font-semibold text-alert" data-id="element-216" data-mp="1xrmmz87yulpt">no dejes pasar esta oportunidad.</strong>
          </p>
        </div>

        <p className="mt-8 text-lg font-semibold text-cocoa-faint line-through sm:text-xl" data-id="element-217" data-mp="1stl34l2puida">
          Precio regular: {offer.regularPrice}
        </p>
        <p className="mt-4 text-sm font-bold uppercase text-alert sm:text-base" data-id="element-218" data-mp="3ssye0h22hq67">Oportunidad única en esta página solo hoy</p>
        <p className="text-sm font-semibold text-forest sm:text-base" data-id="element-219" data-mp="6fq7av9ns2w4">{date}</p>
        <p className="mt-2 flex items-baseline justify-center gap-3" data-id="element-220" data-mp="6hua19vtmvut">
          <span className="font-serif text-6xl font-bold text-leaf-price sm:text-7xl" data-id="element-221" data-mp="be1cutsx7r8j">{offer.price}</span>
          <span className="font-serif text-xl font-semibold text-leaf-soft" data-id="element-222" data-mp="39y19f477e6xs">{offer.currency}</span>
        </p>

        <div className="mt-5 flex items-start gap-3 rounded-xl bg-cream px-4 py-4 text-left" data-id="element-223" data-mp="3exdyzwpgls42">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-forest" data-id="element-224" data-mp="3jwh39exm7uaz">
            <ClapperboardIcon className="h-5 w-5 text-gold" aria-hidden="true" data-id="element-225" data-mp="3ntvcv7p7k43i" />
          </span>
          <div className="text-sm leading-relaxed text-cocoa sm:text-base" data-id="element-226" data-mp="z686hl79hk49">
            <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gold" data-id="element-227" data-mp="1345xmtklegaa">
              <GiftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-228" data-mp="2npvw9q3obhdx" />
              Bonus incluido
            </p>
            <p className="mt-0.5" data-id="element-229" data-mp="2jok4pf18om33">
              Incluye derecho a <strong className="font-semibold" data-id="element-230" data-mp="2eufxqvrz4e20">recibir las actualizaciones del próximo mes</strong>,
              con <strong className="font-semibold" data-id="element-231" data-mp="29vlfpw1tytiy"> más contenidos en videos paso a paso</strong>, concretos y didácticos
              para mejores resultados.
            </p>
            <BonusSlots started={reached} data-id="element-232" data-mp="1zxp28xlf7mho" />
          </div>
        </div>

        <div className="mt-7" data-id="element-233" data-mp="1vwlqonaeocm9">
          <PrimaryButton href={links.checkout} className="rounded-xl" data-id="element-234" data-mp="1vvtv3cgt6evz">
            Sí, quiero desinflamarme hoy
          </PrimaryButton>
        </div>

        <p className="mx-auto mt-4 flex max-w-md items-start justify-center gap-2 text-sm leading-relaxed text-cocoa-soft" data-id="element-235" data-mp="1qzlgc06iwnpc">
          <GlobeIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-236" data-mp="3lbta3a73yyjj" />
          <span data-id="element-237" data-mp="3l9p7cvky45ku">En la próxima página de pago podrás pagar en tu moneda local y verás la conversión automática del precio.</span>
        </p>
        <p className="mx-auto mt-4 max-w-sm border-t border-cream-dark pt-4 text-sm text-cocoa-faint" data-id="element-238" data-mp="2z5k0a1fyyrib">
          Acceso inmediato • Descarga instantánea • Garantía 30 días
        </p>

        <div className="mt-6 border-t border-cream-dark pt-6" data-id="element-239" data-mp="2pf7g4ac2h0cp">
          <p className="text-sm text-cocoa-soft" data-id="element-240" data-mp="2pde8ostmqc3n">Pago disponible con</p>
          <PaymentLogos data-id="element-241" data-mp="2bewsrh9yu3wa" />
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-cocoa" data-id="element-242" data-mp="2bhsqp5ltcskq">
            <span className="inline-flex items-center gap-2" data-id="element-243" data-mp="2feykoypwfiyd">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-olive-100" data-id="element-244" data-mp="21j35p3ymes09">
                <ShieldCheckIcon className="h-4 w-4 text-leaf-price" aria-hidden="true" data-id="element-245" data-mp="26h4ywhprg7pg" />
              </span>
              Garantía 30 días
            </span>
            <span className="inline-flex items-center gap-2" data-id="element-246" data-mp="4prx41j3afxm">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cream-dark" data-id="element-247" data-mp="4n5n7v8q7oc8">
                <ZapIcon className="h-4 w-4 text-cocoa" aria-hidden="true" data-id="element-248" data-mp="1p4y347955zwi" />
              </span>
              Acceso inmediato post-pago
            </span>
          </div>
        </div>
      </div>
    </section>;
}